import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { useEffect, useState } from "react";
import { Plus, RefreshCw, ChevronDown } from "lucide-react";
import type { RecurringExpenseItem } from "@boo/shared";
import { useRecurringExpenseStore } from "./store/useRecurringExpenseStore";
import ExpenseTotalsCard from "./components/ExpenseTotalsCard";
import RecurringExpenseRow from "./components/RecurringExpenseRow";
import RecurringExpenseModal from "./components/RecurringExpenseModal";
import {
  SORT_LABELS,
  RecurringExpenseSort,
} from "./types/recurring-expense.types";
import { Switch } from "@/components/ui/switch";

const SORT_OPTIONS: RecurringExpenseSort[] = ["dueDate", "amount"];

export default function RecurringExpensePage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RecurringExpenseItem | null>(
    null,
  );
  const [inactiveSectionExpanded, setInactiveSectionExpanded] = useState(true);

  function openCreate() {
    setEditingItem(null);
    setModalOpen(true);
  }
  function openEdit(item: RecurringExpenseItem) {
    setEditingItem(item);
    setModalOpen(true);
  }
  function closeModal() {
    setModalOpen(false);
    setEditingItem(null);
  }
  const {
    items,
    monthlyTotal,
    yearlyTotal,
    sort,
    includeInactive,
    loading,
    error,
    loadExpenses,
    setSort,
    setIncludeInactive,
  } = useRecurringExpenseStore();

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const activeItems = items.filter((i) => i.isActive);
  const inactiveItems = items.filter((i) => !i.isActive);

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              Recurring Expenses
            </h1>
            <p className="text-[13px] text-muted-foreground">
              Monthly and yearly commitments
            </p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 rounded-[8px] bg-primary text-primary-foreground px-3 py-2 text-[13px] font-medium hover:bg-primary/90 transition-colors cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>
            Add<span className="hidden sm:inline"> Recurring Expense</span>
          </span>
        </button>
      </div>

      {modalOpen && (
        <RecurringExpenseModal
          onClose={closeModal}
          initialValues={editingItem ?? undefined}
        />
      )}

      <div
        className={`flex flex-col gap-6 transition-opacity duration-200 ${
          loading && items.length > 0 ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        {error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-[13px] text-destructive">
            {error}
          </div>
        )}

        {/* Totals */}
        {activeItems.length > 0 && (
          <ExpenseTotalsCard
            monthlyTotal={monthlyTotal}
            yearlyTotal={yearlyTotal}
          />
        )}

        {/* Main list card */}
        <div className="rounded-[14px] px-5 py-5 sm:px-6 bg-card border-[0.5px] border-border shadow-card">
          {/* Card header */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex gap-1 border-b border-border max-w-full overflow-x-auto scrollbar-none">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setSort(opt)}
                  className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    sort === opt
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {SORT_LABELS[opt]}
                </button>
              ))}
            </div>

            <label className="flex items-center gap-2 text-[13px] text-muted-foreground cursor-pointer select-none">
              <Switch
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
                aria-label="Include inactive"
              />
              Include inactive
            </label>
          </div>

          {loading && items.length === 0 && (
            <SkeletonRegion className="flex flex-col gap-2 mt-4">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-[60px] rounded-[10px]" />
              ))}
            </SkeletonRegion>
          )}

          {/* Empty state */}
          {!loading && activeItems.length === 0 && (
            <div className="py-10 text-center">
              <RefreshCw className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-[14px] font-semibold text-foreground mb-1">
                No recurring expenses yet
              </p>
              <p className="text-[13px] text-muted-foreground">
                Track subscriptions, rent, and other regular commitments here.
              </p>
              <button
                onClick={openCreate}
                className="mt-4 inline-flex items-center gap-1.5 rounded-[8px] border-[0.5px] border-border px-3 py-2 text-[13px] text-muted-foreground hover:text-foreground hover:border-foreground transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                Add your first expense
              </button>
            </div>
          )}

          {/* Active list */}
          {activeItems.length > 0 && (
            <div className="flex flex-col gap-2 mt-4">
              {activeItems.map((item) => (
                <RecurringExpenseRow
                  key={item.id}
                  item={item}
                  onEdit={openEdit}
                />
              ))}
            </div>
          )}

          {/* Inactive section */}
          {includeInactive && inactiveItems.length > 0 && (
            <div className="mt-5 pt-5 border-t border-border">
              <button
                onClick={() => setInactiveSectionExpanded((v) => !v)}
                className="flex items-center gap-2 w-full text-left cursor-pointer group mb-3"
              >
                <span className="text-[12px] font-medium text-muted-foreground uppercase tracking-wide">
                  Inactive ({inactiveItems.length})
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${inactiveSectionExpanded ? "" : "-rotate-90"}`}
                />
              </button>
              {inactiveSectionExpanded && (
                <div className="flex flex-col gap-2">
                  {inactiveItems.map((item) => (
                    <RecurringExpenseRow
                      key={item.id}
                      item={item}
                      onEdit={openEdit}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
